import { formatCount, tt } from '@agos/shared'

/**
 * Every administrator across every franchise — `/franchise-admins`.
 *
 * **FB-P02 confirmed against the running server.** `FranchiseAdminService` gathers this list *"one
 * tenant at a time"* — a loop over `SELECT id, name, slug FROM franchises WHERE is_active = true`, then
 * a query into each tenant's `employees`. Three franchises today, so three round trips per request, and
 * the page refetches after every write. It is a list endpoint whose cost grows with the number of
 * customers the platform has.
 *
 * Not fixable from here, and not to be hidden behind a spinner either: this screen does **not** poll,
 * refetches only after a write, and the cost is filed for the backend.
 *
 * **Measured:** 8 administrators, all 8 in one franchise — 6 `admin` and 2 `main_admin` — and the two
 * other franchises contribute none. A list that simply omitted them could not be told from one that
 * failed to reach them, so every active franchise is named and the empty ones say so.
 *
 * **The payload carries no password field**, checked rather than assumed. What it carries is real
 * people's names and e-mail addresses, so nothing here is logged, and the e2e assert on counts and
 * shapes rather than on anybody's name.
 */

export interface FranchiseAdmin {
  id: number
  name: string
  email: string
  systemRole: string
  active: boolean
  franchiseId: number
  franchiseName: string
  franchiseSlug: string
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function adminsOf(payload: unknown): FranchiseAdmin[] {
  const rows = Array.isArray(payload) ? payload : ((payload as { admins?: unknown })?.admins ?? [])
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name),
        email: text(row.email),
        systemRole: text(row.systemRole),
        active: row.active !== false,
        franchiseId: typeof row.franchiseId === 'number' ? row.franchiseId : 0,
        franchiseName: text(row.franchiseName),
        franchiseSlug: text(row.franchiseSlug),
      },
    ]
  })
}

export interface FranchiseGroup {
  franchiseId: number
  franchiseName: string
  admins: FranchiseAdmin[]
}

/**
 * Grouped by franchise, **including the ones with nobody**.
 *
 * That is the point: two of the three franchises have no administrator, and a list that drops them
 * cannot be told from one that failed to reach them. The caller passes the franchises it knows about.
 */
export function groupedByFranchise(
  admins: readonly FranchiseAdmin[],
  franchises: readonly { id: number; name: string; isActive: boolean }[],
): FranchiseGroup[] {
  return franchises
    .filter((franchise) => franchise.isActive)
    .map((franchise) => ({
      franchiseId: franchise.id,
      franchiseName: franchise.name,
      admins: admins.filter((admin) => admin.franchiseId === franchise.id),
    }))
    .sort((left, right) => right.admins.length - left.admins.length)
}

/** Said on a franchise with nobody, so an absence reads as an absence. */
export function noAdminsNote(): string {
  return tt('Táto pobočka nemá žiadneho administrátora - nikto sa do jej manažéra nedostane.')
}

/**
 * Said above the list, because the cost is a fact about this endpoint and not about the network.
 *
 * `GET /super-admin/admins` is an N+1 across tenant databases. Naming it is what stops the next person
 * adding a poll to it.
 */
export function loadNote(groups: readonly FranchiseGroup[]): string {
  return `${formatCount(groups.length)} ${tt('pobočiek sa načítava po jednej - zoznam sa preto neobnovuje sám, len po zmene.')}`
}

// ─── the two writes ──────────────────────────────────────────────────────────

/**
 * Deactivating, which legacy called deleting — FB-P02's other half.
 *
 * The endpoint's own summary is *"Deactivate an administrator"*, and `GET /super-admin/admins` selects
 * `active` precisely so a switched-off account stays visible. Legacy's confirmation says *zmazať*,
 * which teaches an operator that the action is irreversible when it is not — and somebody who believes
 * a mistake is permanent hesitates over the wrong things.
 */

export function deactivateTitle(): string {
  return tt('Deaktivovať administrátora?')
}

export function deactivateDescription(admin: FranchiseAdmin): string {
  return `${admin.email} · ${admin.franchiseName} - ${tt('účet zostane v zozname a dá sa znova zapnúť. Nemaže sa.')}`
}

/** `PATCH …/admins/{id}` takes the password, and this screen never stores or echoes it. */
export function passwordPayload(password: string): Record<string, unknown> {
  return { password }
}

export function reactivatePayload(): Record<string, unknown> {
  return { active: true }
}

/** What stops a password change. The server's rule; the confirmation is this screen's. */
export function passwordProblems(password: string, repeated: string): string[] {
  const found: string[] = []
  if (password.length < 6) found.push(tt('Heslo musí mať aspoň 6 znakov'))
  else if (password !== repeated) found.push(tt('Heslá sa nezhodujú'))
  return found
}

/**
 * Named to the account, because eight administrators of one franchise can be two characters apart.
 *
 * The e-mail rather than the name: two people can share a name and nobody shares an address.
 */
export function passwordConfirmation(admin: FranchiseAdmin): string {
  return `${tt('Nastaviť nové heslo pre')} ${admin.email} (${admin.franchiseName})?`
}
